import { Alert } from 'src/design/atoms/Alert';
import { Container } from 'src/design/atoms/Container';
import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { IconLock } from '@tabler/icons-react';
import { nanoid } from 'nanoid';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WorkflowDto,
  WorkflowRequestDto,
  WorkflowStepDto,
} from 'src/api/FiretrolApiClient';
import { useSaveWorkflowTemplate } from 'src/api/queries/workflows/useWorkflowTemplateMutations';
import { useSaveJobWorkflow } from 'src/api/queries/workflows/useJobWorkflowMutations';
import { useJobs } from 'src/hooks/useJobs';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { getErrorMessage, showSuccessToast } from 'src/utils';
import { WorkflowDivider } from './WorkflowDivider';
import { WorkflowPreview } from './WorkflowPreview';
import { WorkflowSettings } from './WorkflowSettings';
import { WorkflowStep } from './WorkflowStep';

const initialData: Partial<WorkflowStepDto> = {
  excludedAssignedUsers: [],
  assignedRoles: [],
  assignedUsers: [],
  notifyRoles: [],
  notifyUsers: [],
  excludedNotifyUsers: [],
  approvalUsers: [],
  approvalRoles: [],
  excludedApprovalUsers: [],
  notifyAssignees: true,
  notifyOnSignedEdit: false,
  allowReject: false,
  allowRevise: false,
  allowVoid: false,
  buttonColor: 'primary',
};

export interface IEditorStep {
  key: string;
  step: WorkflowStepDto;
}

/** Editor-local row key; unsaved steps have no server id and their key is never sent to the API. */
const editorStepKey = (step: WorkflowStepDto) =>
  step.id > 0 ? `step-${step.id}` : nanoid();

const toEditorSteps = (steps: WorkflowStepDto[]): IEditorStep[] =>
  steps.map((step) => ({ key: editorStepKey(step), step }));

/** The API treats stepNumber as the ordering key, so it must match the editor row order on save. */
const toRequestSteps = (steps: WorkflowStepDto[]): WorkflowStepDto[] =>
  steps.map((step, idx) => ({
    ...step,
    stepNumber: idx,
    isLastStep: idx === steps.length - 1,
  }));

export interface ICreateWorkflowProps {
  initialSettings: WorkflowDto;
  initialSteps: WorkflowStepDto[];
  isTemplateEditor?: boolean;
  onSaveComplete?: (workflow?: WorkflowDto) => void;
  deleteWorkflow?: () => Promise<void>;
  onNavigate: () => void;
  readOnly?: boolean;
}

/** @registry Workflow builder — settings panel plus an editable, ordered list of workflow steps, with save/preview/cancel. */
export const CreateWorkflow: React.FC<ICreateWorkflowProps> = ({
  initialSettings,
  onSaveComplete,
  initialSteps,
  isTemplateEditor = false,
  deleteWorkflow,
  onNavigate,
  readOnly = false,
}) => {
  const navigate = useNavigate();
  const { selectedJob } = useJobs();
  const [settings, setSettings] = React.useState<WorkflowDto>(initialSettings);
  const [editorSteps, setEditorSteps] = React.useState<IEditorStep[]>(() =>
    toEditorSteps(initialSteps),
  );
  const steps = React.useMemo(
    () => editorSteps.map((entry) => entry.step),
    [editorSteps],
  );
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const saveTemplateMutation = useSaveWorkflowTemplate();
  const saveJobWorkflowMutation = useSaveJobWorkflow();
  const saving =
    saveTemplateMutation.isPending || saveJobWorkflowMutation.isPending;

  const onSettingsChanged = (key: string, value: any) => {
    setSettings((prevState) => {
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  const onAddClick = (idx: number) => {
    const stepToAdd: WorkflowStepDto = {
      id: 0,
      stepNumber: idx,
      editable: true,
      isExternalStep: false,
      isLastStep: false,
      attachPdfToNotification: false,
      isEmployeeReviewStep: false,
      createPdf: false,
      label: '<Status Name>',
      buttonText: '<Button Text>',
      ...initialData,
    } as WorkflowStepDto;
    setEditorSteps((prevState) => [
      ...prevState.slice(0, idx),
      { key: nanoid(), step: stepToAdd },
      ...prevState.slice(idx),
    ]);
  };

  const onRemove = (key: string) => {
    setEditorSteps((prevState) =>
      prevState.filter((entry) => entry.key !== key),
    );
  };

  const onValueChange = (key: string, updated: WorkflowStepDto) => {
    setEditorSteps((prevState) =>
      prevState.map((entry) =>
        entry.key === key ? { ...entry, step: updated } : entry,
      ),
    );
  };

  const toWorkflowRequest = (): WorkflowRequestDto => ({
    ...settings,
    requestSteps: toRequestSteps(steps),
  });

  const saveTemplate = async () => {
    setSaveError(null);
    const isCreate = initialSettings.id === -1;
    try {
      const result = await saveTemplateMutation.mutateAsync({
        templateId: settings.id,
        request: toWorkflowRequest(),
      });
      onSaveComplete?.();
      showSuccessToast(
        `Workflow Template ${isCreate ? 'Created' : 'Updated'}!`,
      );
      if (isCreate) {
        navigate(`/settings/workflows/${result.id}`);
      }
    } catch (err: unknown) {
      setSaveError(getErrorMessage(err));
    }
  };

  const onSaveClicked = async () => {
    if (readOnly) return;
    if (isTemplateEditor) {
      return saveTemplate();
    }
    if (!selectedJob) return;
    setSaveError(null);
    try {
      const result = await saveJobWorkflowMutation.mutateAsync({
        jobId: selectedJob.id,
        workflowId: String(settings.id),
        request: toWorkflowRequest(),
      });
      showSuccessToast('Workflow Updated!');
      onSaveComplete?.(result);
    } catch (err: unknown) {
      setSaveError(getErrorMessage(err));
    }
  };

  const renderWorkflowSteps = () => {
    return editorSteps.map(({ key, step }, idx) => {
      return (
        <React.Fragment key={key}>
          <WorkflowStep
            stepKey={key}
            step={step}
            locked={idx === 0 || idx === editorSteps.length - 1}
            onValueChange={(updated) => onValueChange(key, updated)}
            onStepRemoved={() => onRemove(key)}
            isTemplateEditor={isTemplateEditor}
            resourceId={settings.resourceId}
            readOnly={readOnly}
          />
          {!step.isLastStep && (
            <WorkflowDivider
              showButton={!readOnly}
              onButtonClick={() => onAddClick(idx + 1)}
            />
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <Container size="md">
      <Stack gap="lg">
        {readOnly && (
          <Alert color="blue" icon={<IconLock size={18} />}>
            This is a system workflow. It is managed by the system and cannot be
            edited or deleted.
          </Alert>
        )}
        <WorkflowSettings
          settings={settings}
          onChange={onSettingsChanged}
          isTemplateEditor={isTemplateEditor}
          onNavigate={onNavigate}
          deleteWorkflow={deleteWorkflow}
          readOnly={readOnly}
        />
        <Divider />
        {renderWorkflowSteps()}
        <WorkflowPreview
          isOpen={previewOpen}
          setOpen={setPreviewOpen}
          steps={steps}
          settings={settings}
        />
        {saveError && (
          <Alert title="Error" color="red">
            {saveError}
          </Alert>
        )}
        <Paper p="md" withBorder>
          <Group justify="center" gap="xs">
            <SecondaryButton
              onClick={() => setPreviewOpen(true)}
              disabled={saving}
            >
              Preview
            </SecondaryButton>
            <SecondaryButton
              onClick={() => {
                window.location.pathname.includes('/settings/workflows')
                  ? navigate('/settings/workflows')
                  : navigate(-1);
              }}
              disabled={saving}
            >
              Cancel
            </SecondaryButton>
            {!readOnly && (
              <SaveButton onClick={onSaveClicked} loading={saving}>
                Save Workflow
              </SaveButton>
            )}
          </Group>
        </Paper>
      </Stack>
    </Container>
  );
};
